import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Wallet, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function MyPortfolio({ agents }) {
  const { t } = useTranslation();
  const [allocations, setAllocations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [amount, setAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAllocations = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/allocations/me`, { withCredentials: true });
      setAllocations(res.data);
    } catch (err) {
      console.error('Error fetching allocations:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAllocations(); }, [fetchAllocations]);

  const handleAllocate = async () => {
    if (!selectedAgent || !amount || Number(amount) <= 0) return;
    setActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/allocations/allocate`, {
        agent_id: selectedAgent, amount: Number(amount)
      }, { withCredentials: true });
      setAmount('');
      fetchAllocations();
    } catch (err) {
      console.error('Allocation error:', err);
    }
    setActionLoading(false);
  };

  const handleWithdraw = async (agentId, maxAmount) => {
    const withdrawAmount = prompt(`Withdraw from ${agentId} (max $${maxAmount.toLocaleString()}):`);
    if (!withdrawAmount || Number(withdrawAmount) <= 0) return;
    try {
      await axios.post(`${BACKEND_URL}/api/allocations/withdraw`, {
        agent_id: agentId, amount: Number(withdrawAmount)
      }, { withCredentials: true });
      fetchAllocations();
    } catch (err) {
      console.error('Withdraw error:', err);
    }
  };

  const tradingAgents = agents.filter(a => !['MASTER', 'SUPERVIVENCIA'].includes(a.type));
  const summary = allocations?.summary || {};
  const allocs = allocations?.allocations || [];

  return (
    <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6" data-testid="my-portfolio">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-500" />
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('wallet.myPortfolio')}</h3>
        </div>
        {summary.total_invested > 0 && (
          <div className="flex items-center gap-4 text-xs font-mono">
            <span className="text-slate-400">Invested: <span className="text-white">${summary.total_invested?.toLocaleString()}</span></span>
            <span className={summary.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
              P&L: {summary.total_pnl >= 0 ? '+' : ''}${summary.total_pnl?.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <select value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)}
          className="flex-1 bg-[#11141D] border border-white/10 text-white px-3 py-2 font-mono text-xs focus:border-white/25 outline-none"
          data-testid="allocation-agent-select">
          <option value="">{t('wallet.selectAgent')}</option>
          {tradingAgents.map(a => (
            <option key={a.id} value={a.id}>{a.id} - {a.name}</option>
          ))}
        </select>
        <input type="number" placeholder="Amount ($)" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-32 bg-[#11141D] border border-white/10 text-white px-3 py-2 font-mono text-xs focus:border-white/25 outline-none"
          data-testid="allocation-amount-input" />
        <button onClick={handleAllocate} disabled={actionLoading || !selectedAgent || !amount}
          className="px-4 py-2 bg-emerald-500 text-black font-bold text-xs hover:bg-emerald-400 transition-colors disabled:opacity-50"
          data-testid="allocate-btn">
          {actionLoading ? '...' : 'ASIGNAR'}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <RefreshCw className="w-5 h-5 text-slate-500 animate-spin" />
        </div>
      ) : allocs.length === 0 ? (
        <div className="text-center py-6">
          <Wallet className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-500 text-xs font-mono">{t('wallet.noAllocationsDesc')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.agent')}</th>
                <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.invested')}</th>
                <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('wallet.currentValue')}</th>
                <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">P&L</th>
                <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.return')}</th>
                <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('wallet.action')}</th>
              </tr>
            </thead>
            <tbody>
              {allocs.map(a => (
                <tr key={a.agent_id} className="border-b border-white/5 hover:bg-[#11141D] transition-colors">
                  <td className="py-2 px-2">
                    <span className="font-mono text-xs text-slate-400">{a.agent_id}</span>
                    <span className="text-white text-xs ml-2">{a.agent_name}</span>
                  </td>
                  <td className="py-2 px-2 text-right font-mono text-white text-xs">${a.amount?.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right font-mono text-white text-xs">${a.current_value?.toLocaleString()}</td>
                  <td className={`py-2 px-2 text-right font-mono text-xs ${a.simulated_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {a.simulated_pnl >= 0 ? '+' : ''}${a.simulated_pnl?.toLocaleString()}
                  </td>
                  <td className={`py-2 px-2 text-right font-mono text-xs ${a.agent_return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {a.agent_return_pct >= 0 ? '+' : ''}{a.agent_return_pct}%
                  </td>
                  <td className="py-2 px-2 text-center">
                    <button onClick={() => handleWithdraw(a.agent_id, a.amount)}
                      className="px-2 py-1 bg-red-500/20 text-red-400 text-[10px] font-mono hover:bg-red-500/30 transition-colors"
                      data-testid={`withdraw-btn-${a.agent_id}`}>
                      RETIRAR
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
